import React, { useCallback, useEffect, useState, useMemo } from "react";

import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";

import { DetailModal, TransactionModal } from "./FinancialModals";

import {
  CustomerType,
  FindBankAccountRequestType,
  FindBankAccountResponseType,
  GetConfigsResponseType,
  GetCustomerResponseType,
  ReactSelectOptionType,
  ResponseType,
  SuspendTransactionRequestType,
  TransactionListRequestType,
  TransactionListResponseType,
  TransactionType,
} from "./FiMaTypes";

import useAxios from "../../services/useAxios";

import CustomerList from "./CustomerList";
import TransactionList from "./TransactionList";
import Tabs from "../tab/Tabs";
import { DateObject } from "react-multi-date-picker";
import Swal from "sweetalert2";

type SelectedCustomerType = {
  info: CustomerType;
  transactions: TransactionType[];
};

enum TransactionModalsTypes {
  Close = 0,
  AddTransaction = 1,
  AddCard = 2,
  DetailModal = 3,
}

interface FinancialContextType {
  transactions: TransactionType[];
  customers: CustomerType[];
  selectedCustomer: SelectedCustomerType;
  openDetailModal: (
    customer: CustomerType,
    state:
      | TransactionModalsTypes.AddTransaction
      | TransactionModalsTypes.DetailModal
  ) => void;
  suspendTransaction: (id: number, customer_id: number) => void;
}

const FinancialContext = React.createContext<FinancialContextType>({
  transactions: [],
  customers: [],
  selectedCustomer: {
    info: {
      id: 0,
      uid: 0,
      name: "",
      creditor: 0,
      indebtedness: 0,
      status: 0,
    },
    transactions: [],
  },
  openDetailModal: (customer, state) => {},
  suspendTransaction: (id, customer_id) => {},
});

const FinancialContextProvider: React.FC = ({}) => {
  // API Connections
  const { execute: getCustomersAPI } = useAxios("api2/get_customers");
  const { execute: transactionsListAPI } = useAxios("api2/transactions_list");
  const { execute: getConfigsAPI } = useAxios("api2/get_configs");
  const { execute: suspendTransactionAPI } = useAxios(
    "api2/suspend_transaction"
  );

  // modal show state
  const [showModal, setShowModal] = useState<TransactionModalsTypes>(
    TransactionModalsTypes.Close
  );

  // data state
  const [transactions, setTransactions] = useState<TransactionType[]>([]);
  const [customers, setCustomers] = useState<CustomerType[]>([]);

  // selected customer state
  const [selectedCustomer, setSelectedCustomer] =
    useState<SelectedCustomerType>({
      info: {
        id: 0,
        uid: 0,
        name: "",
        creditor: 0,
        indebtedness: 0,
        status: 0,
      },
      transactions: [],
    });

  // Bank Names state(getting from config file from server)
  const [banks, setBanks] = useState<ReactSelectOptionType[]>([]);

  // tabs information
  const navItem = useMemo(
    () => [
      { title: "لیست مشتریان", target: "#nav-pill-financial-customer-tab" },
      {
        title: "لیست تراکنش‌ها",
        target: "#nav-pill-financial-transactions-tab",
      },
    ],
    []
  );

  const tabContent = useMemo(
    () => [
      {
        id: "nav-pill-financial-customer-tab",
        component: (
          <div
            style={{
              width: "70vw",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: "90%",
              }}
            >
              <CustomerList />
            </div>
          </div>
        ),
      },
      {
        id: "nav-pill-financial-transactions-tab",
        component: (
          <div
            style={{
              width: "70vw",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: "90%",
              }}
            >
              <TransactionList />
            </div>
          </div>
        ),
      },
    ],
    []
  );

  // modal state changeHandlers
  const openDetailModal = useCallback(
    (
      customer: CustomerType,
      state:
        | TransactionModalsTypes.AddTransaction
        | TransactionModalsTypes.DetailModal
    ) => {
      setSelectedCustomer({
        info: customer,
        transactions: transactions.filter((tran) => +tran.uid === +customer.id),
      });
      setShowModal(state);
    },
    [transactions]
  );

  // get transactions
  const getTransactions = useCallback(
    async (customer_id: number | undefined = undefined) => {
      const data: TransactionListRequestType = {};
      if (customer_id) {
        data.customer_id = "" + customer_id;
      }

      return await transactionsListAPI(data).then(
        (res: TransactionListResponseType) => {
          let resTran: TransactionType[] = res.transactions.map((t) => {
            let date;
            if ("" + t.mtm !== "") {
              date = new DateObject({
                date: +t.mtm * 1000,
                calendar: persian,
                locale: persian_fa,
              });
            }
            const isReceiver = +t.customer_id === +t.to_account.customer_id;

            return {
              id: t.bank_transaction__id,
              uid: t.customer_id,
              payDate: date ? date.format("YYYY/MM/DD") : "",
              payTime: date ? date.format("HH:mm") : "",
              payType: +t.type,
              bankName: isReceiver ? t.to_account.bank : t.from_account.bank,
              accountNumber: isReceiver
                ? t.to_account.account_number
                : t.from_account.account_number,
              status: +t.status,
            };
          });
          setTransactions(resTran);
          return resTran;
        }
      );
    },
    []
  );

  // suspend transaction
  const suspendTransaction = useCallback(
    (id: number, customer_id: number) => {
      Swal.fire({
        title: "تعلیق تراکنش",
        text: "آیا از تعلیق این تراکنش مطمئن هستید؟",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "بله",
        cancelButtonText: "خیر",
      }).then((result) => {
        if (result.isConfirmed) {
          const data: SuspendTransactionRequestType = {
            transaction_id: "" + id,
          };
          suspendTransactionAPI(data).then(async (res: ResponseType) => {
            if (+res.status === 1) {
              // TODO: change here
              let res = await getTransactions();
              if (showModal === TransactionModalsTypes.DetailModal) {
                setSelectedCustomer((prev) => ({
                  ...prev,
                  transactions: res
                    ? res?.filter((t) => +t.uid === +customer_id)
                    : [],
                }));
              }
            }
          });
        }
      });
    },
    [getTransactions, showModal]
  );

  // const createTransaction = useCallback(() => {

  // }, [])

  useEffect(() => {
    getCustomersAPI().then((res: GetCustomerResponseType) => {
      setCustomers(
        res.customers.map<CustomerType>((customer) => {
          let name = `${customer.firstname} ${customer.lastname}`;
          if (+customer.corporate === 1) {
            name = `${customer.company_name} (${name})`;
          }

          return {
            id: customer.id,
            uid: customer.main_uid,
            name,
            creditor: customer.amount_credit,
            indebtedness: customer.amount_owed,
            status: customer.status,
          };
        })
      );
    });
    getConfigsAPI().then((res: GetConfigsResponseType) => {
      setBanks(res.banks);
    });
    getTransactions(); // TODO: change it
  }, []);

  const value: FinancialContextType = {
    transactions,
    customers,
    selectedCustomer,
    openDetailModal,
    suspendTransaction,
  };
  return (
    <FinancialContext.Provider {...{ value }}>
      <div className="container-xl d-flex justify-content-center align-items-center">
        <Tabs
          className=""
          navItem={navItem}
          type="horizontal"
          // myFunction={getTransactions} // TODO: change it
          myFunction={() => {}}
          tabContent={tabContent}
        />
      </div>
      <DetailModal
        selectedCustomer={selectedCustomer}
        show={showModal}
        setShow={setShowModal}
      />
      <TransactionModal
        show={showModal}
        customer_id={selectedCustomer.info.id}
        banks={banks}
        setShow={setShowModal}
      />
    </FinancialContext.Provider>
  );
};

export default FinancialContext;
export { FinancialContextProvider, TransactionModalsTypes };
export type { SelectedCustomerType };
